import { useCallback, useContext, useEffect } from 'react';

import { message } from 'antd';
import dayjs from 'dayjs';

import { Activities, BeneficiariesForm } from './components';

import {
  AdditionalDataSection,
  ClientDataSection,
  PaymentMethodSection,
  QuotationStepButtons,
  ValiditySection,
} from '../../../../../../../shared';

import QuotationService from '../../../../../../../../../../../shared/services/quotation';

import { AuthContext } from '../../../../../../../../../../../shared/contexts';
import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../../../shared/contexts';
import {
  BINARY_VALUES,
  InsertInsuredPolicyholder,
  SendPaymentMethodRequest,
  SendProposalRequest,
} from '../../../../../../../../../../../shared/interfaces';
import { QuotationStep } from '../../../../../../../shared/interfaces';
import { StepFourFormContext } from '../../../../contexts/StepFourForm';
import { StepThreeFormContext } from '../../../../../StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../../../Wizard/contexts/WizardDataForm';

import { displayErrorMessage, formattedDateVigency } from '../../../../../../../../../../../shared/utils';
import EmailService, { buildSendMailRequest } from '../../../../../../../../../../../shared/services/email';
import { getQuoteDocumentBase64, transformToPdfQuote } from '../../../../../../../utils';
import { BranchDocumentsURL, BudgetType } from '../../../../../../../utils/quotation-documents.enum';
import { QuotationContextData } from '../../../../../../../../../../../shared/interfaces/services/quote-pdf-service';
import { ContentTypeValue, MESSAGES_SENDMAIL } from '../../../../../../../../../../../shared/interfaces/services/email-services';
import { CompanyData } from '../../../../../../../../../../../shared/interfaces/services/company-data-enum';

export const FormIssue = () => {
  const {
    setCanShowConfirmIssue,
    quotationInfo,
    setCurrentStep,
    setQuotationInfo,
  } = useContext(QuotationInfoContext);
  const {
    provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, allCoveragesPerPlan, paymentMethodsList,
    additionalDataSectionForm,
    clientDataSectionForm,
    listCreditCardInsured,
    paymentMethodFormValues,
    paymentMethodSectionForm,
    setAdditionalDataFormValues,
    setclientDataFormValues,
    setPaymentMethodFormValues,
    setCanShowSpinner,
    documentTypeList,
  } = useContext(GenericDataFormContext);
  const {
    activitiesSectionForm,
    beneficiaries,
    errorCreditCardMessage,
    methodSelectedFromStepThree,
    setActivitiesFormValues,
    setErrorCreditCardMessage,
    setMethodSelectedFromStepThree,
    validitySectionForm,
  } = useContext(StepFourFormContext);
  const authContext = useContext(AuthContext);
  const { stepOneForm, clientSelected } = useContext(StepOneFormContext);
  const { propertyDataForm, stepTwoForm, apActiviesListOptions, productsList, activities } = useContext(StepTwoFormContext);
  const { paymentMethodsForm, quotationResponse, activitiesForm, } = useContext(StepThreeFormContext);
  const { quotationForm, ageRangeList, relationshipList, } = useContext(WizardDataFormContext);

  const dateExpiredCreditCard = dayjs(paymentMethodFormValues?.expiration);

  const paymentMethodExpirationMonth = dateExpiredCreditCard.month() + 1;
  const paymentMethodExpirationYear = dateExpiredCreditCard.year();

  const sendClientData = useCallback(async () => {
    const {
      fullName,
      societyType,
      genderType,
      perceptionAgent,
      ivaCondition,
      address,
      documentNumber,
      documentType,
      cuit,
      nationality,
      inscriptionDate,
      inscriptionNumber,
      contractDate,
    } = clientDataSectionForm.getFieldsValue();

    const { cbuNumber, cardNumber, companyCreditCard } =
      paymentMethodSectionForm.getFieldsValue();

    const {
      profession,
      civilStatus,
      economicActivity,
      addressEmail,
      rutaNumber,
      cuilNumber,
      telephoneNumber,
      telephoneNumberJob,
      cellphoneNumber,
      webPage,
      birthDate,
      placeOfBirth,
      countryBirth,
    } = additionalDataSectionForm.getFieldsValue();

    const params: InsertInsuredPolicyholder = {
      numeroCotizacion: Number(quotationInfo?.token!),
      codigoAsegurado: Number(quotationForm?.cliente?.nroAsegurado),
      nombreApellido: fullName,
      tipoDeSociedad: societyType,
      profesion: profession,
      genero: genderType,
      estadoCivil: civilStatus,
      actividadEconomica: economicActivity,
      mail: addressEmail,
      agenteDePercepcion: perceptionAgent,
      cbuDePago: cbuNumber,
      cbuParaSiniestros: cbuNumber,
      nroDeRuta: rutaNumber,
      inscripcionIva: ivaCondition,
      domicilio: {
        calleYNro: address,
        codigoPostal: Number(quotationForm?.cliente?.codigoPostal),
        sufijoPostal: Number(quotationForm?.cliente?.sufijoCodigoPostal),
      },
      documento: {
        tipoDocumento: documentType,
        numeroDocumento: documentNumber,
        cuit: cuit,
        cuil: cuilNumber,
      },
      telefonos: {
        particular: telephoneNumber,
        laboral: telephoneNumberJob,
        celular: cellphoneNumber,
        paginaWeb: webPage,
      },
      nacimiento: {
        fecha: birthDate ? dayjs(birthDate).format('DD-MM-YYYY') : "01-01-1999",
        lugar: placeOfBirth || '',
        codigoPais: countryBirth || 0,
        codigoNacionalidad: nationality || 0,
      },
      inscripcion: {
        fecha: dayjs(inscriptionDate).format('DD-MM-YYYY'),
        numero: inscriptionNumber,
        fechaDeContrato: dayjs(contractDate).format('DD-MM-YYYY'),
      },
    };

    if (cardNumber) {
      params.tarjetaDeCredito = {
        idEmpresa: companyCreditCard!,
        numero: cardNumber,
        anioFin: paymentMethodExpirationYear,
        mesFin: paymentMethodExpirationMonth,
      };
    }

    try {
      await QuotationService.postInsertInsuredPolicyholder(params);
    } catch (error: any) {
      setErrorCreditCardMessage(error.message);
    }
  }, [
    clientDataSectionForm,
    additionalDataSectionForm,
    paymentMethodSectionForm,
    setErrorCreditCardMessage,
    quotationForm,
    quotationInfo,
  ]);

  const sendProposal = useCallback(async () => {
    const userName =
      authContext?.getUserInfo()?.intermediarioPrincipal?.nombreInter;

    const { startDate, endDate } = formattedDateVigency(validitySectionForm.getFieldValue('validityDate'));

    const params: SendProposalRequest = {
      numeroCotizacion: parseInt(quotationInfo?.token!),
      fechaDesde: startDate,
      fechaHasta: endDate,
      usuario: userName,
    };

    try {
      const response = await QuotationService.postSendProposal(params);

      setQuotationInfo((prev) => ({
        ...prev!,
        proposalNumber: response.data.numeroDePropuesta,
      }));
      await invokeSendEmail();
      setCanShowConfirmIssue(true);
    } catch (error) { }
  }, [
    quotationInfo,
    validitySectionForm,
    setQuotationInfo,
    setCanShowConfirmIssue,
    authContext
  ]);

  const numberCreditCard = String(paymentMethodFormValues?.cardNumber);

  const creditCardFiltered = listCreditCardInsured?.tarjetasDelAsegurado.find(
    (tarjeta) => tarjeta.numeroTarjeta === numberCreditCard
  );

  const codeMethodPayment = methodSelectedFromStepThree?.codigoFormaDePago;

  const insertActivitiesInsureds = useCallback(async () => {
    if (!activities?.length) return;

    const requests: Array<Promise<any>> = [];
    let componentCounter = 1;

    activities.forEach((activityItem) => {
      const people = activityItem?.people ?? [];

      people.forEach((person: any) => {
        const quotationActivity = quotationForm?.actividades?.find(
          (quotationActivity) =>
            String(quotationActivity.codigo) === String(activityItem.activity)
        );

        const birthDateValue = person?.birthdayDateValue
          ? Number(person.birthdayDateValue)
          : person?.birthdayDate
            ? Number(dayjs(person.birthdayDate, 'DD-MM-YYYY').format('YYYYMMDD'))
            : 0;

        const params = {
          token: Number(quotationInfo?.token),
          rama: Number(quotationInfo?.branch),
          arse: Number(quotationInfo?.arse),
          componente: componentCounter,
          codigoRiesgo: Number(activityItem.relationship),
          nombre: person.fullName,
          tipoDocumento: Number(person.documentTypeValue ?? person.documentType),
          numeroDocumentoAlfa: String(person.documentNumber || '0'),
          fechaNacimiento: birthDateValue,
          nacionalidad: person.nationality,
          actividad: Number(activityItem.activity),
          categoria: Number(quotationActivity?.categoria ?? 0),
          cuil: person.cuil ? Number(person.cuil) : Number(BINARY_VALUES.ZERO),
          sexo: Number(person.genderValue ?? 0),
        };

        componentCounter += 1;

        requests.push(QuotationService.postInsertInsuredPayrollLife(params));
      });
    });

    if (requests.length) {
      await Promise.all(requests);
    }
  }, [activities, quotationInfo, quotationForm]);

  const insertBeneficiaries = useCallback(async () => {
    if (!beneficiaries.length) return;

    const requests = beneficiaries.map((beneficiary, index) => {
      let tipoDocumento: string;
      let numeroDocumento: string;
      let cuitCuil: string;

      if (beneficiary.personType === 'JURIDICA') {
        const noDeterminadoOption = documentTypeList?.find(
          (el) => el.label?.toUpperCase().includes('NO DETERMINADO') || el.label?.toUpperCase().includes('NO_DETERMINADO')
        );
        tipoDocumento = noDeterminadoOption?.value || '0';
        numeroDocumento = '0';
        cuitCuil = beneficiary.cuil || '0';
      } else {
        tipoDocumento = String(beneficiary.documentType);
        numeroDocumento = beneficiary.documentNumber || '0';
        cuitCuil = '0';
      }

      return QuotationService.postInsertBeneficiaryLife({
        numeroCotizacion: quotationInfo?.token!,
        arse: quotationInfo?.arse!,
        rama: quotationInfo?.branch!,
        nombreApellido: beneficiary.fullName,
        tipoDocumento,
        numeroDocumento,
        cuitCuil,
        clausulaNoRepeticion: beneficiary.hasNoRepetitionClause,
        tomador: beneficiary.holder || false,
        secuencia: String(index + 1),
      });
    });

    await Promise.all(requests);
  }, [beneficiaries, quotationInfo, documentTypeList]);

  const syncInsuredsAndBeneficiaries = useCallback(async () => {
    await insertActivitiesInsureds();
    await insertBeneficiaries();
  }, [insertActivitiesInsureds, insertBeneficiaries]);

  const sendPaymentMethod = useCallback(async () => {
    const { cbuNumber, cardNumber, companyCreditCard } =
      paymentMethodSectionForm.getFieldsValue();

    const creditCard = {
      idEmpresa: companyCreditCard ?? 0,
      numero: cardNumber || 0,
      anioFin: creditCardFiltered?.anioHasta ?? paymentMethodExpirationYear,
      mesFin: creditCardFiltered?.mesHasta ?? paymentMethodExpirationMonth,
    };

    const params: SendPaymentMethodRequest = {
      numeroCotizacion: parseInt(quotationInfo?.token!),
      codigoFormaDePago: codeMethodPayment,
      numeroCbu: cbuNumber || 0,
      tarjetaDeCredito: creditCard,
    };

    try {
      await QuotationService.postInsertPaymentData(params);
    } catch (error: any) {
      paymentMethodSectionForm.setFields([{ name: 'cbuNumber', errors: [error.message] }]);
      throw error;
    }
  }, [
    paymentMethodSectionForm,
    creditCardFiltered,
    paymentMethodExpirationYear,
    quotationInfo,
    codeMethodPayment,
  ]);

  const handlerOnClick = useCallback(async () => {
    try {
      setCanShowSpinner(true);
      await clientDataSectionForm.validateFields();
      await activitiesSectionForm.validateFields();
      await additionalDataSectionForm.validateFields();
      await paymentMethodSectionForm.validateFields();

      setclientDataFormValues(clientDataSectionForm.getFieldsValue());
      setActivitiesFormValues(activitiesSectionForm.getFieldsValue());
      setAdditionalDataFormValues(additionalDataSectionForm.getFieldsValue());
      setPaymentMethodFormValues(paymentMethodSectionForm.getFieldsValue());



      await syncInsuredsAndBeneficiaries();
      await Promise.all([sendClientData(), sendPaymentMethod()]);

      await sendProposal();
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    setclientDataFormValues,
    clientDataSectionForm,
    setActivitiesFormValues,
    activitiesSectionForm,
    setAdditionalDataFormValues,
    additionalDataSectionForm,
    setPaymentMethodFormValues,
    paymentMethodSectionForm,
    setCanShowSpinner,
    sendProposal,
    sendClientData,
    sendPaymentMethod,
    syncInsuredsAndBeneficiaries,
    beneficiaries,
  ]);

  const handlerBackButtonOnClick = useCallback(() => {
    setclientDataFormValues(clientDataSectionForm.getFieldsValue());
    setActivitiesFormValues(activitiesSectionForm.getFieldsValue());
    setAdditionalDataFormValues(additionalDataSectionForm.getFieldsValue());
    setPaymentMethodFormValues(paymentMethodSectionForm.getFieldsValue());
    setCurrentStep(QuotationStep.THREE);
  }, [
    setclientDataFormValues,
    clientDataSectionForm,
    setActivitiesFormValues,
    activitiesSectionForm,
    setAdditionalDataFormValues,
    additionalDataSectionForm,
    setPaymentMethodFormValues,
    paymentMethodSectionForm,
    setCurrentStep,
  ]);

  useEffect(() => {
    if (errorCreditCardMessage) {
      message.error(errorCreditCardMessage);
      setErrorCreditCardMessage('');
    }
  }, [errorCreditCardMessage, errorCreditCardMessage]);

  const invokeSendEmail = async () => {

    const ctx: QuotationContextData = {
      quotationForm,
      quotationInfo,
      clientSelected,
      stepOneForm,
      stepTwoForm, propertyDataForm, apActiviesListOptions, productsList,
      activitiesForm, paymentMethodsForm,
      quotationResponse, ageRangeList, relationshipList, paymentMethodsList,
      provinceList, allLocalitiesPerProvince, ivaConditionList, paymentMethods, allCoveragesPerPlan,
      branchDocumentsURL: BranchDocumentsURL.AP
    };
    try {
      const article = ctx.quotationInfo?.article;
      const distributionListKey = article 
        ? "PROPUESTA_" + String(article).padStart(6, '0')
        : "PROPUESTA_000000";
      const result = await getQuoteDocumentBase64(transformToPdfQuote(ctx, BudgetType.PRODUCTOR), BranchDocumentsURL.AP);
      if (result) {
        const payload = await buildSendMailRequest({
          subject: `Cotización ${ctx.quotationForm?.token} - ${CompanyData.COMPANY_NAME}`,
          body: 'Copia del Productor',
          includeAttachments: true,
          attachmentBase64: result.base64,
          attachmentName: result.fileName,
          attachmentMimeType: ContentTypeValue.PDF,
          saveToSentItems: true,
        }, distributionListKey);

        if (payload) await EmailService.sendMailViaLambda(payload);
      }
    } catch (error) {
      displayErrorMessage(MESSAGES_SENDMAIL.ERROR_MESSAGE);
    }
  }

  return (
    <>
      <ValiditySection
        validitySectionForm={validitySectionForm}
        stepTwoForm={propertyDataForm}
        quotationForm={quotationForm}
      />

      <ClientDataSection />

      <AdditionalDataSection />

      <PaymentMethodSection
        paymentMethodsForm={paymentMethodsForm}
        setMethodSelectedFromStepThree={setMethodSelectedFromStepThree}
      />

      <Activities />

      <BeneficiariesForm />

      <QuotationStepButtons
        label='Enviar'
        onClick={handlerOnClick}
        onPressBackButton={handlerBackButtonOnClick}
      />
    </>
  );
};
